/**
 * DateRangeBar — shared CRM date-range filter (iter-140)
 *
 * Extracted from CrmAnalytics so the same pill-based preset bar can be
 * reused on /crm/payments and any future CRM page that needs the same
 * Today / Last 7 days / Custom Range UX.
 *
 * Props
 *   value      : { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
 *   onChange   : ({ start, end, presetId, label }) => void
 *   presetId   : optional controlled preset id (defaults to 'last7')
 *   onPresetChange : optional handler when preset changes
 *   presets    : optional list of preset ids to include; default = ALL_PRESET_IDS
 *   rightSlot  : optional ReactNode rendered on the right of the bar
 *                (e.g. an export-PDF button on Analytics)
 *   loading    : optional boolean to show 'refreshing…' indicator
 *   testId     : optional data-testid for the wrapping container
 *
 * The component is fully controlled — callers manage state and re-fetch on
 * preset/date changes.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const todayISO = () => new Date().toISOString().slice(0, 10);
const subDaysISO = (days) => new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
const monthStartISO = (offsetMonths = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths, 1);
  return d.toISOString().slice(0, 10);
};
const monthEndISO = (offsetMonths = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths + 1, 0);
  return d.toISOString().slice(0, 10);
};

const ALL_PRESETS = [
  { id: 'today', label: 'Today', range: () => ({ start: todayISO(), end: todayISO() }) },
  { id: 'yesterday', label: 'Yesterday', range: () => ({ start: subDaysISO(1), end: subDaysISO(1) }) },
  { id: 'last7', label: 'Last 7 days', range: () => ({ start: subDaysISO(7), end: todayISO() }) },
  { id: 'lastWeek', label: 'Last Week', range: () => ({ start: subDaysISO(14), end: subDaysISO(7) }) },
  { id: 'thisMonth', label: 'This Month', range: () => ({ start: monthStartISO(0), end: todayISO() }) },
  { id: 'lastMonth', label: 'Last Month', range: () => ({ start: monthStartISO(-1), end: monthEndISO(-1) }) },
  { id: 'thisQuarter', label: 'This Quarter', range: () => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3);
    const start = new Date(now.getFullYear(), q * 3, 1).toISOString().slice(0, 10);
    return { start, end: todayISO() };
  } },
  { id: 'lastQuarter', label: 'Last Quarter', range: () => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3) - 1;
    const year = q < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const adjQ = (q + 4) % 4;
    const start = new Date(year, adjQ * 3, 1).toISOString().slice(0, 10);
    const end = new Date(year, adjQ * 3 + 3, 0).toISOString().slice(0, 10);
    return { start, end };
  } },
  { id: 'thisYear', label: 'This Year', range: () => ({
    start: `${new Date().getFullYear()}-01-01`, end: todayISO(),
  }) },
  { id: 'all', label: 'All Time', range: () => ({ start: '', end: '' }) },
];

export const DEFAULT_PAYMENTS_PRESETS = ['today', 'yesterday', 'last7', 'thisMonth', 'lastMonth', 'thisYear', 'all'];
export const DEFAULT_ANALYTICS_PRESETS = ['last7', 'lastWeek', 'lastMonth', 'thisQuarter', 'lastQuarter', 'thisYear', 'all'];

export { todayISO, subDaysISO, monthStartISO, monthEndISO };

export const DateRangeBar = ({
  value,
  onChange,
  presetId: controlledPresetId,
  onPresetChange,
  presets,
  rightSlot,
  loading = false,
  testId = 'date-range-bar',
  className = '',
}) => {
  // Filter the master preset list down to the requested ids (keeps order).
  const presetList = useMemo(() => {
    const ids = presets || DEFAULT_PAYMENTS_PRESETS;
    return ALL_PRESETS.filter((p) => ids.includes(p.id));
  }, [presets]);

  // Internal preset id state (falls back if not controlled).
  const [internalPresetId, setInternalPresetId] = useState(controlledPresetId || presetList[0]?.id || 'last7');
  const activePresetId = controlledPresetId ?? internalPresetId;

  // Custom-range editor state — pre-fill from value or 30-day default.
  const [customStart, setCustomStart] = useState(value?.start || subDaysISO(30));
  const [customEnd, setCustomEnd] = useState(value?.end || todayISO());

  useEffect(() => {
    if (activePresetId === 'custom') return;
    const p = ALL_PRESETS.find((pp) => pp.id === activePresetId);
    if (!p) return;
    const r = p.range();
    setCustomStart(r.start || customStart);
    setCustomEnd(r.end || customEnd);
    onChange?.({ start: r.start, end: r.end, presetId: activePresetId, label: p.label });
  }, [activePresetId]);

  const setPreset = (id) => {
    if (id === activePresetId) return;
    if (!controlledPresetId) setInternalPresetId(id);
    onPresetChange?.(id);
  };

  const emitCustom = (start, end) => {
    onChange?.({ start, end, presetId: 'custom', label: `${start} → ${end}` });
  };

  const rangeLabel = useMemo(() => {
    if (activePresetId === 'custom') return `${customStart} → ${customEnd}`;
    if (activePresetId === 'all') return 'All time';
    return ALL_PRESETS.find((p) => p.id === activePresetId)?.label || '';
  }, [activePresetId, customStart, customEnd]);

  return (
    <div className={`bg-white rounded-2xl border border-[#E8E5DF] p-4 ${className}`} data-testid={testId}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="w-4 h-4 text-[#FF3B6B]" />
          <span className="text-xs uppercase font-bold text-[#6B6B7A] tracking-wider mr-1">Range</span>
          {presetList.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                activePresetId === p.id
                  ? 'bg-[#FF3B6B] text-white'
                  : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'
              }`}
              data-testid={`range-preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPreset('custom')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              activePresetId === 'custom'
                ? 'bg-[#FF3B6B] text-white'
                : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'
            }`}
            data-testid="range-preset-custom"
          >
            Custom Range
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            {activePresetId === 'custom' && (
              <>
                <div>
                  <Label className="text-[10px] text-[#6B6B7A]">From</Label>
                  <Input
                    type="date"
                    value={customStart}
                    max={customEnd}
                    onChange={(e) => { setCustomStart(e.target.value); emitCustom(e.target.value, customEnd); }}
                    className="h-8 text-xs rounded-xl border-[#E8E5DF]"
                    data-testid="range-custom-start"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B6B7A]">To</Label>
                  <Input
                    type="date"
                    value={customEnd}
                    min={customStart}
                    max={todayISO()}
                    onChange={(e) => { setCustomEnd(e.target.value); emitCustom(customStart, e.target.value); }}
                    className="h-8 text-xs rounded-xl border-[#E8E5DF]"
                    data-testid="range-custom-end"
                  />
                </div>
              </>
            )}
            <span className="text-xs text-[#6B6B7A]">
              Currently showing: <strong className="text-[#1E1E24]">{rangeLabel}</strong>
              {loading && <span className="ml-2 text-[#FF3B6B]">refreshing…</span>}
            </span>
          </div>
          {rightSlot}
        </div>
      </div>
    </div>
  );
};

export default DateRangeBar;
